/**
 * useGameSync — Realtime / polling sync for the GameContext (PR-V3.E split).
 *
 * Responsibilities (only these — SRP):
 *   1. Resolve `gameRef` from `token` via `findGame`.
 *   2. Fetch the initial game payload + stock for that gameRef.
 *   3. Open a Supabase Realtime channel scoped to the gameRef and update
 *      `gameData` from incoming payloads (game-row fields only: status,
 *      current, start, pairing_code).
 *   4. Poll `getGame` at POLL_INTERVAL as a long-interval safety net.
 *   5. Clean up the Realtime channel + polling interval on unmount or when
 *      gameRef changes.
 *
 * Note: the per-game player list and answer list now live in the dedicated
 * `useGamePlayers` / `useGameAnswers` hooks (audit M8 / B7). The polling
 * dirty-check therefore no longer compares the legacy `players` /
 * `answers` JSONB columns on the `game` row.
 *
 * KNOWN LATENT BUGS — out of scope for PR-V3.E (see PR description, follow-ups):
 *   - The dirty-check compares `prev?.start !== fresh.start` on the
 *     RAW (un-normalised) value. Equality between an ISO string and an
 *     `{ isoString }` object is therefore inconsistent across formats.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import type { SupabaseClient, RealtimeChannel } from "@supabase/supabase-js"

import { POLL_INTERVAL } from "../../utils/gameConfig"
import { findGame } from "../../utils/findGame"
import { getGame, getStockItems } from "../../lib/supabase/games"
import { sizedUnsplashUrl } from "../../utils/imageSizing"
import type { StockItem } from "../../types"
import type { GameData } from "./types"

interface UseGameSyncResult {
  gameRef: string | null
  setGameRef: React.Dispatch<React.SetStateAction<string | null>>
  gameData: GameData | null
  stock: StockItem[] | null
  refetchGame: () => Promise<void>
}

/**
 * Sync hook driven by `token` (set by the consumer) and a Supabase client.
 * Returns the gameRef setter so the Provider can keep `setGameRef` exposed
 * on the context (backward compatibility).
 */
export const useGameSync = (
  token: string | null,
  supabase: SupabaseClient | null
): UseGameSyncResult => {
  const subscriptionRef = useRef<RealtimeChannel | null>(null)
  const [gameRef, setGameRef] = useState<string | null>(null)
  const [gameData, setGameData] = useState<GameData | null>(null)
  const [stock, setStock] = useState<StockItem[] | null>(null)

  const refetchGame = useCallback(async () => {
    if (gameRef && supabase) {
      const gameDataResult = await getGame(gameRef, supabase)
      setGameData(gameDataResult as unknown as GameData)
    }
  }, [gameRef, supabase])

  const subscribeToGame = useCallback(
    (gameId: string) => {
      if (!supabase) return

      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current)
        subscriptionRef.current = null
      }

      const channel = supabase
        .channel(`game-${gameId}`)
        .on(
          "postgres_changes" as const,
          {
            event: "*",
            schema: "public",
            table: "game",
            filter: `id=eq.${gameId}`,
          },
          (payload: any) => {
            if (payload.new) {
              setGameData(payload.new as GameData)
            }
          }
        )
        .subscribe((subscriptionStatus: string) => {
          if (subscriptionStatus === "CHANNEL_ERROR") {
            console.error(
              "[Realtime] Subscription error - check Supabase Realtime settings"
            )
          }
        })

      subscriptionRef.current = channel
    },
    [supabase]
  )

  // Token -> gameRef resolution
  useEffect(() => {
    const fetchData = async () => {
      const ref = await findGame(token!)
      setGameRef(ref)
    }
    if (token) {
      fetchData()
    }
  }, [token])

  // Initial load + Realtime subscription + long-interval safety-net poll
  // (audit M3): Realtime is the primary source; the 10 s poll exists only
  // to catch silently-dropped websocket payloads. The structural
  // dirty-check below skips the React state update when content has not
  // actually changed, so a no-op poll stays a single SELECT round-trip.
  useEffect(() => {
    if (!supabase) return

    let pollInterval: number | null = null

    const fetchData = async () => {
      const gameDataResult = await getGame(gameRef!, supabase)
      setGameData(gameDataResult as unknown as GameData)

      subscribeToGame(gameRef!)

      const stockItems = await getStockItems(gameRef!, supabase)
      const filteredItems = (stockItems || []).filter(
        item => item.url !== undefined
      )

      // Only warm the cache for the FIRST photo (eager mass-preload of all
      // 10 originals was up to ~20 MB on a fresh game; the Carousel handles
      // current+1 lookahead on its own as `current` advances).
      if (typeof window !== "undefined" && filteredItems[0]?.url) {
        new Image().src = sizedUnsplashUrl(filteredItems[0].url, 1600)
      }

      setStock(filteredItems)

      pollInterval = window.setInterval(async () => {
        try {
          const freshData = await getGame(gameRef!, supabase)
          if (freshData) {
            setGameData(prev => {
              const fresh = freshData as unknown as GameData
              if (
                prev?.status !== fresh.status ||
                prev?.current !== fresh.current ||
                prev?.start !== fresh.start ||
                prev?.pairing_code !== fresh.pairing_code
              ) {
                return fresh
              }
              return prev
            })
          }
        } catch (err) {
          console.error("[GameContext] Polling error:", err)
        }
      }, POLL_INTERVAL)
    }
    if (gameRef) {
      fetchData()
    }

    return () => {
      if (subscriptionRef.current && supabase) {
        supabase.removeChannel(subscriptionRef.current)
        subscriptionRef.current = null
      }
      if (pollInterval) {
        window.clearInterval(pollInterval)
      }
    }
  }, [gameRef, supabase, subscribeToGame])

  return { gameRef, setGameRef, gameData, stock, refetchGame }
}
